import { AxiosResponse } from 'axios'
import { type Event, EventSourcePolyfill } from 'event-source-polyfill'

import { DateRange } from '@/components/DateRangePicker'
import { env } from '@/env'
import { api, handleUnauthorizedSession } from '@/lib/axios'
import { SseFetchTransport } from '@/lib/sse-fetch-transport'
import {
  CancelOrderType,
  FinalAmountType,
  MultipleMerchantsOrdersType,
  OrderType,
  TimeDriverArrivesType,
} from '@/types/orders.types'
import type { VoucherListType } from '@/types/super-partner.types'

const getOrders = (
  { start, end }: DateRange,
  uuid: string
): Promise<AxiosResponse<OrderType[]>> => {
  return api.post<OrderType[]>(`/merchants/${uuid}/orders`, {
    start,
    end,
  })
}

const getMultipleMerchantOrders = (
  { start, end }: DateRange,
  ids: string[]
): Promise<AxiosResponse<MultipleMerchantsOrdersType[]>> | void => {
  if (ids.length > 0)
    return api.get<MultipleMerchantsOrdersType[]>(
      `/multiple-merchants/orders?merchants=${ids.join(
        ','
      )}&start=${start}&end=${end}`
    )
}

const updateOrderStatus = ({
  status,
  orderId,
  amount,
}: {
  status: string
  orderId?: string
  amount?: number
}): Promise<AxiosResponse<OrderType>> => {
  if (!orderId) return Promise.reject(new Error('Order id is required'))

  const payload: { action: string; amount?: number } = {
    action: status,
  }

  if (amount !== undefined) {
    payload.amount = amount
  }

  return api.post<OrderType>(`/orders/${orderId}`, payload)
}

const getOrdersByShortId = (
  shortId: string
): Promise<AxiosResponse<OrderType[]>> => {
  if (!shortId) return Promise.reject(new Error('Short id is required'))
  return api.get<OrderType[]>(`/orders/short/${shortId}`)
}

const getCompensationVouchers = async (
  orderId: string
): Promise<VoucherListType> => {
  const response = await api.get<VoucherListType>(
    `/orders/${orderId}/compensation`
  )
  return response.data
}

const cancelOrder = async (
  orderId: string,
  cancelOrder: CancelOrderType
): Promise<CancelOrderType> => {
  const response = await api.post<CancelOrderType>(`/orders/${orderId}`, {
    ...cancelOrder,
  })

  return response.data
}

const getSimulationTotalAmountCharge = async (
  orderId: string,
  amount: number
): Promise<FinalAmountType> => {
  const response = await api.post<FinalAmountType>(
    `/orders/${orderId}/compensation/simulation`,
    {
      amount,
    }
  )
  return response.data
}

export function getOrderByIdStream(
  orderId: string,
  signal?: AbortSignal
): Promise<OrderType> {
  return new Promise((resolve, reject) => {
    const eventSource = new EventSourcePolyfill(
      `${env.VITE_API_URL}/orders/${orderId}`,
      {
        withCredentials: true,
        headers: {
          Authorization: api.defaults.headers.Authorization as string,
        },
        heartbeatTimeout: 310000, // server sends a heartbeat every 300000ms; stay above that so we don't reconnect on a heartbeat that's simply in flight
        Transport: SseFetchTransport,
      }
    )

    // One-shot read: the stream only feeds this promise, so it has to be
    // closed on every exit path. Leaving it open kept reconnecting in the
    // background on the shopper tablets, and each failed reconnect surfaced
    // as an unhandled rejection with no stacktrace. Live updates go through
    // subscribeOrderById instead.
    let settled = false
    const close = () => {
      if (settled) return
      settled = true
      eventSource.close()
    }

    signal?.addEventListener('abort', close, { once: true })

    eventSource.addEventListener('order', (event: Event) => {
      const response = event as Event & { data: string }
      close()

      if (!response.data) {
        resolve({} as OrderType)
        return
      }

      try {
        resolve(JSON.parse(response.data) as OrderType)
      } catch (error) {
        console.warn('Failed to parse order stream event payload:', error)
        reject(
          new Error(`Order stream failed for order ${orderId}: invalid payload`)
        )
      }
    })

    eventSource.addEventListener('error', (event: Event) => {
      const errorEvent = event as Event & {
        error?: Error
        status?: number
        statusText?: string
      }

      if (errorEvent.error) {
        // The polyfill emits this shape while retrying its own heartbeat
        // timeout; let it reconnect instead of tearing the stream down.
        if (errorEvent.error.message?.includes('Reconnecting')) return

        close()
        reject(
          new Error(
            `Order stream failed for order ${orderId}: ${errorEvent.error.message}`
          )
        )
        return
      }

      // No `.error` means the polyfill's onStart rejected the response
      // itself (non-200 status or wrong Content-Type) and already closed.
      close()

      if (errorEvent.status === 401) {
        handleUnauthorizedSession()
        return
      }

      reject(
        new Error(
          `Order stream failed for order ${orderId}: HTTP ${errorEvent.status ?? 'unknown'} ${errorEvent.statusText ?? ''}`.trim()
        )
      )
    })
  })
}

async function getTimeTripDriver(orderId: string) {
  const { data } = await api.get<TimeDriverArrivesType>(
    `/orders/${orderId}/trip`
  )
  return data
}

export default {
  getOrders,
  getSimulationTotalAmountCharge,
  getMultipleMerchantOrders,
  updateOrderStatus,
  getOrdersByShortId,
  getCompensationVouchers,
  cancelOrder,
  getOrderByIdStream,
  getTimeTripDriver,
}
